/**
 * MockEventPage - public landing page for the free timed "Alpha Mock" event.
 * Countdown + seat reservation (email capture feeds the marketing engine).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  CalendarDays, Clock, BarChart3, Users, CheckCircle2, ArrowRight,
  Timer, Target, Percent, Loader2,
} from "lucide-react";

const useCountdown = (targetIso) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!targetIso) return;
    const tick = () => {
      const diff = new Date(targetIso) - new Date();
      if (diff <= 0) { setLeft({ days: 0, hours: 0, mins: 0, secs: 0, past: true }); return; }
      setLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor(diff / 3600000) % 24,
        mins: Math.floor(diff / 60000) % 60,
        secs: Math.floor(diff / 1000) % 60,
        past: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return left;
};

const CountdownBlock = ({ value, label }) => (
  <div className="flex flex-col items-center rounded-2xl bg-card border border-border px-4 py-3 min-w-[76px] shadow-sm">
    <span className="text-3xl sm:text-4xl font-bold tabular-nums text-primary">{String(value).padStart(2, "0")}</span>
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</span>
  </div>
);

export default function MockEventPage() {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reserved, setReserved] = useState(false);
  const left = useCountdown(info?.event_at);

  useEffect(() => {
    api.get("/mock-event/info").then(setInfo).catch(() => {});
    document.title = "Free ADC Part 1 Mock Exam - The Alpha Mock | PassADC";
  }, []);

  const reserve = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await api.post("/mock-event/reserve", { email, name: name || null });
      setReserved(true);
      setInfo((i) => (i ? { ...i, seats_reserved: r.seats_reserved } : i));
      toast.success(r.already_reserved ? "You're already on the list!" : "Seat reserved - see you there!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reservation failed - please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const dateLabel = info?.event_at
    ? new Date(info.event_at).toLocaleString("en-AU", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
        hour: "numeric", minute: "2-digit", timeZone: "Australia/Sydney",
      }) + " AEST"
    : "";

  return (
    <div data-testid="mock-event-page" className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary" data-testid="mock-event-logo">PassADC</Link>
          <Link to="/signup" className="text-sm font-medium text-muted-foreground hover:text-foreground">
            Explore the platform →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-14 pb-10">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-accent/10 text-accent border border-accent/20 rounded-full px-3 py-1">
          <CalendarDays className="w-3.5 h-3.5" /> Free live event
        </span>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
          {info?.title || "The Alpha Mock"}
          <span className="block text-primary mt-1">Your ADC Part 1 dress rehearsal.</span>
        </h1>
        <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl">
          {info?.subtitle || "Free timed ADC Part 1 mock exam - 140 questions, 3 hours, blueprint-weighted."}
          {" "}Sit it with everyone else, get a per-subject score and your percentile against the whole cohort.
        </p>
        {dateLabel && (
          <p className="mt-3 text-sm font-semibold flex items-center gap-2" data-testid="mock-event-date">
            <Clock className="w-4 h-4 text-primary" /> {dateLabel}
          </p>
        )}

        {/* Countdown */}
        {left && !left.past && (
          <div data-testid="mock-countdown" className="mt-8 flex gap-3 flex-wrap">
            <CountdownBlock value={left.days} label="days" />
            <CountdownBlock value={left.hours} label="hours" />
            <CountdownBlock value={left.mins} label="mins" />
            <CountdownBlock value={left.secs} label="secs" />
          </div>
        )}
        {left?.past && (
          <p className="mt-8 text-lg font-semibold text-accent">The mock is live - check your inbox for the link.</p>
        )}

        {/* Reservation */}
        <div className="mt-10 max-w-xl">
          {reserved ? (
            <div data-testid="mock-reserve-success" className="rounded-2xl border border-success/30 bg-success/10 p-6">
              <div className="flex items-center gap-2 font-semibold text-success">
                <CheckCircle2 className="w-5 h-5" /> Your seat is reserved
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                We'll email your mock exam link on the day. Meanwhile, take the free
                15-question readiness check to see where you stand.
              </p>
              <Link
                to="/diagnostic"
                data-testid="mock-success-diagnostic-link"
                className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90"
              >
                Take the readiness check <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={reserve} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="text-sm font-semibold mb-3">Reserve your free seat - 30 seconds</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="mock-reserve-name"
                  placeholder="First name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input sm:w-44"
                />
                <input
                  data-testid="mock-reserve-email"
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input flex-1"
                />
                <button
                  data-testid="mock-reserve-btn"
                  type="submit"
                  disabled={submitting || !email}
                  className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Reserve my seat
                </button>
              </div>
              <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                <span data-testid="mock-seats-count">
                  {info?.seats_reserved > 0
                    ? `${info.seats_reserved} candidate${info.seats_reserved === 1 ? "" : "s"} already reserved`
                    : "No card. No cost. Unsubscribe anytime."}
                </span>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* What you get */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-muted-foreground mb-6">
            Exactly like the real paper
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Target, title: "140 questions", desc: "Exam-style MCQs drawn from a 1,200+ question bank." },
              { icon: Timer, title: "3 hours, timed", desc: "Real exam pressure - the rhythm most candidates never practise." },
              { icon: BarChart3, title: "Blueprint-weighted", desc: "Weighted to the official ADC blueprint." },
              { icon: Percent, title: "Score + percentile", desc: "Per-subject breakdown and where you rank in the cohort." },
            ].map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-5">
                <f.icon className="w-6 h-6 text-primary" />
                <div className="font-semibold mt-3">{f.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-muted-foreground mb-6">
          How it works
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            ["Reserve your seat", "Just your email. Free, no card, 30 seconds."],
            ["Get the link on the day", "We email your personal mock link when doors open. It stays open 24 hours."],
            ["Sit it & see your standing", "Instant per-subject score, percentile, and what to fix before the real thing."],
          ].map(([t, d], i) => (
            <div key={t} className="rounded-2xl border border-border bg-card p-5">
              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center">{i + 1}</div>
              <div className="font-semibold mt-3">{t}</div>
              <div className="text-sm text-muted-foreground mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-muted-foreground mb-6">
            Quick answers
          </h2>
          <div className="space-y-4 max-w-2xl">
            {[
              ["Is it really free?", "Yes. No card, no trial, no catch. It's our way of showing you what PassADC feels like under real conditions."],
              ["Do I need a PassADC account?", "We'll set one up from your reservation email so your results are saved - it takes zero extra steps."],
              ["What if I can't make the exact time?", "The mock link stays open for 24 hours, so you can sit it any time that day."],
              ["Will I see the answers afterwards?", "You'll get your per-subject score and percentile instantly, plus explanations for every question you got wrong."],
            ].map(([q, a]) => (
              <div key={q} className="rounded-xl border border-border bg-card p-4">
                <div className="font-semibold text-sm">{q}</div>
                <div className="text-sm text-muted-foreground mt-1">{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        PassADC · Independent ADC Part 1 prep · <Link to="/" className="underline">passadc.com</Link>
      </footer>
    </div>
  );
}
